import type { ApiResponse } from '@/types';

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!envUrl) return '/api';
  if (envUrl.endsWith('/api')) return envUrl;
  return envUrl.replace(/\/$/, '') + '/api';
}

const API_BASE_URL = getApiBaseUrl();

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  const data: ApiResponse<T> = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Unknown error');
  }
  
  return data.data as T;
}

// ============================================
// TYPES
// ============================================

export interface PaymentMethod {
  id: 'paykeeper' | 'stars';
  name: string;
  description: string;
  icon: string;
  available: boolean;
}

export interface PayKeeperPaymentResult {
  paymentId: string;
  paymentUrl: string;
  invoiceId: string;
}

export interface StarsPaymentResult {
  invoiceLink: string;
}

// ============================================
// API CALLS
// ============================================

export async function getPaymentMethods(): Promise<{ methods: PaymentMethod[] }> {
  return request('/payments/methods');
}

export async function createPayKeeperPayment(
  planId: string,
  period: 'monthly' | 'yearly'
): Promise<PayKeeperPaymentResult> {
  return request('/payments/paykeeper/create', {
    method: 'POST',
    body: JSON.stringify({ planId, period }),
  });
}

export async function createStarsPayment(
  planId: string,
  period: 'monthly' | 'yearly'
): Promise<StarsPaymentResult> {
  return request('/payments/stars/create', {
    method: 'POST',
    body: JSON.stringify({ planId, period }),
  });
}
